import type { SnakeCase } from "type-fest";
import type { ZodType } from "zod";
import z from "zod";

import { looseUuid } from "@thalamos/common";

import { isLaterDay, today } from "../assessments/calendarDay.js";
import { isReasonNeededForEndingCause } from "../rules/isReasonNeededForEndingCause.js";
import {
  OfferedEndingCauseSchema,
  PersonInvolvementEndedCauseSchema,
  type OfferedEndingCause,
  type PersonInvolvementEndedCause,
} from "./involvementEnding.js";
import {
  OrganisationIdSchema,
  type OrganisationId,
  type OrganisationIdUnbranded,
} from "./organisation.js";
import { PersonIdSchema, type PersonIdUnbranded } from "./person.js";
import type { CustomerRole } from "./thalamos.js";
import { UserIdSchema, type UserId, type UserIdUnbranded } from "./user.js";

export type PersonInvolvementIdUnbranded = string;
export const PersonInvolvementIdSchema =
  looseUuid().brand<"haven-common:PersonInvolvementId">() satisfies ZodType<PersonInvolvementIdUnbranded>;
/**
 * One recorded involvement between a young person and a party. Branded so it
 * cannot be passed where a `PersonId` is expected; both are UUIDs.
 */
export type PersonInvolvementId = z.infer<typeof PersonInvolvementIdSchema>;

export type PersonInvolvementEndingIdUnbranded = string;
export const PersonInvolvementEndingIdSchema =
  looseUuid().brand<"haven-common:PersonInvolvementEndingId">() satisfies ZodType<PersonInvolvementEndingIdUnbranded>;
/**
 * One ending of an involvement: the row holding who ended it, why and from
 * when. Branded apart from `PersonInvolvementId` because an ending and the
 * involvement it ends are both UUIDs, named side by side in the trail.
 */
export type PersonInvolvementEndingId = z.infer<
  typeof PersonInvolvementEndingIdSchema
>;

/**
 * The kinds of involvement a young person's record recognises.
 *
 * Closed here rather than in the column, as the role set is, so admitting
 * another kind is a line of TypeScript. Written out as a union as well as a
 * schema because tsoa follows a type into the API contract but cannot follow
 * `z.infer`.
 */
export type PersonInvolvementKind =
  | "created_by"
  | "duty_of_care"
  | "placement"
  | "responsible_user"
  | "designated_assessor"
  | "assessor"
  | "viewer"
  | "approver"
  | "shared_assessment_reader";

export const PersonInvolvementKindSchema = z.enum([
  "created_by",
  "duty_of_care",
  "placement",
  "responsible_user",
  "designated_assessor",
  "assessor",
  "viewer",
  "approver",
  "shared_assessment_reader",
]) satisfies ZodType<PersonInvolvementKind & SnakeCase<PersonInvolvementKind>>;

/**
 * What may be done to a young person's record on the strength of an
 * involvement, or of the administrative membership.
 *
 * `assign` is the authority to start and end involvements, which is what makes
 * the graph govern the graph rather than a role reaching every child.
 * `allocate_responsible_user` is narrower and holds only while nobody runs the
 * case (see `isPersonUnallocated`): the organisation that created the person's
 * record allocating one, without which no involvement could ever be started.
 *
 * `assess` is one action and not two, because an assessment is one person's:
 * there is no partial completion and no joint completion, so "may add to an
 * assessment" and "may finish one" are the same capability. What distinguishes
 * the designated assessor is not what they may do but that theirs is the
 * definitive assessment, which the involvement's kind says and its cardinality
 * enforces.
 *
 * `approve` is separate from reading for the same reason `merge` is: an
 * approval is a decision somebody is answerable for, and a definitive
 * assessment is the only kind that needs one.
 *
 * `read_log` is reading the record of what has been done to a child — who
 * opened the record, who corrected it, who set their case up, who was given or
 * denied reach over them, and each assessment of them as it was worked. It is deliberately not implied by `read`: the two answer different
 * questions, and the second is accountability rather than care. Somebody
 * contributing an assessment needs what the record says about the child; who
 * has been given reach over them is the responsible body's business.
 *
 * `read_assessments` is reading what each finished assessment about the child
 * says: the answers and comments. It is not implied by `read`, which sees the
 * record and which assessments there are and where each stands. Deciding who
 * works on a child is not a reason to read what they wrote, so administering
 * conveys the one and not the other: an admin reads only what their
 * organisation's part in the child's care lets anybody there read. An
 * assessment still under way is read only by whoever works on it;
 * `mayReadAssessmentContents` combines that with `read_assessments`.
 *
 * `read_identity_withheld` is reading everything about how a record is
 * handled except who the child is: its state, who opened it and who is
 * involved, never a name, date of birth, address or identifier. No involvement
 * conveys it; the administrative membership holds it in place of `read`
 * (`personActionsForThalamosAdmin`). It names a property rather than a view, so
 * whatever is gated on it must disclose nothing identifying, and an endpoint a
 * holder of `read` must reach asks for `read`.
 *
 * `involve_guest` is starting a guest's involvement: asking somebody outside
 * every organisation to complete an assessment, or sharing assessments with
 * them. It is separate from `assign` because the people around a child who may
 * ask a guest are not only the one who decides who else is involved; see
 * `docs/decisions/2026-10-31-a-case-manager-designated-assessor-or-approver-may-involve-a-guest.md`.
 */
export type ConveyedPersonAction =
  | "read"
  | "read_identity_withheld"
  | "read_assessments"
  | "read_log"
  | "update"
  | "merge"
  | "assess"
  | "approve"
  | "assign"
  | "allocate_responsible_user"
  | "involve_guest";

export const ConveyedPersonActionSchema = z.enum([
  "read",
  "read_identity_withheld",
  "read_assessments",
  "read_log",
  "update",
  "merge",
  "assess",
  "approve",
  "assign",
  "allocate_responsible_user",
  "involve_guest",
]) satisfies ZodType<ConveyedPersonAction & SnakeCase<ConveyedPersonAction>>;

/**
 * The party an involvement is with: an organisation, or a person.
 *
 * A guest is not a third member. They are a `user` party whose
 * `onBehalfOfOrganisationId` is absent.
 */
export type PersonInvolvementPartyUnbranded =
  | {
      kind: "organisation";
      organisationId: OrganisationIdUnbranded;
      name: string;
    }
  | {
      kind: "user";
      userId: UserIdUnbranded;
      name: string;
      onBehalfOfOrganisationId: OrganisationIdUnbranded | null;
      onBehalfOfOrganisationName: string | null;
    };

export const PersonInvolvementPartySchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("organisation"),
    organisationId: OrganisationIdSchema,
    name: z.string(),
  }),
  z.object({
    kind: z.literal("user"),
    userId: UserIdSchema,
    name: z.string(),
    onBehalfOfOrganisationId: OrganisationIdSchema.nullable(),
    onBehalfOfOrganisationName: z.string().nullable(),
  }),
]) satisfies ZodType<PersonInvolvementPartyUnbranded>;

export type PersonInvolvementParty = z.infer<
  typeof PersonInvolvementPartySchema
>;

export type PersonInvolvementUnbranded = {
  id: PersonInvolvementIdUnbranded;
  personId: PersonIdUnbranded;
  kind: PersonInvolvementKind;
  party: PersonInvolvementPartyUnbranded;
  approvesUserId: UserIdUnbranded | null;
  approvesUserName: string | null;
  startedByUserName: string;
  startedAt: Date;
  expiresAt: Date | null;
  endedAt: Date | null;
  endedCause: PersonInvolvementEndedCause | null;
  endedByUserName: string | null;
  /** Empty for an involvement that has ended or expired: it conveys nothing. */
  actions: ConveyedPersonAction[];
};

export const PersonInvolvementSchema = z
  .object({
    id: PersonInvolvementIdSchema,
    personId: PersonIdSchema,
    kind: PersonInvolvementKindSchema,
    party: PersonInvolvementPartySchema,
    approvesUserId: UserIdSchema.nullable(),
    approvesUserName: z.string().nullable(),
    startedByUserName: z.string(),
    startedAt: z.coerce.date(),
    expiresAt: z.coerce.date().nullable(),
    endedAt: z.coerce.date().nullable(),
    endedCause: PersonInvolvementEndedCauseSchema.nullable(),
    endedByUserName: z.string().nullable(),
    actions: z.array(ConveyedPersonActionSchema),
  })
  .brand<"haven-common:PersonInvolvement">() satisfies ZodType<PersonInvolvementUnbranded>;

/**
 * One involvement, as the API reports it.
 *
 * `actions` is derived by the server from the kind and the holder's role, never
 * stored on the row and never recomputed by a client. A page that composed its
 * own account of what an involvement means would be a second implementation of
 * the policy, and its failure mode is that it is reassuring and wrong.
 */
export type PersonInvolvement = z.infer<typeof PersonInvolvementSchema>;

export type AllocateResponsibleUserRequestUnbranded = {
  userId: UserIdUnbranded;
  organisationId: OrganisationIdUnbranded;
};

const ResponsibleUserNamedSchema = z.object({
  userId: UserIdSchema,
  organisationId: OrganisationIdSchema,
});

export const AllocateResponsibleUserRequestSchema =
  ResponsibleUserNamedSchema.brand<"haven-common:AllocateResponsibleUserRequest">() satisfies ZodType<AllocateResponsibleUserRequestUnbranded>;

/**
 * Naming who runs a case nobody runs yet: the person, and the organisation they
 * run it for, which is often not the body that opened the record.
 */
export type AllocateResponsibleUserRequest = z.infer<
  typeof AllocateResponsibleUserRequestSchema
>;

export type ReplaceResponsibleUserRequestUnbranded =
  AllocateResponsibleUserRequestUnbranded & {
    /** The involvement being handed on, as the person sending this saw it. */
    involvementId: PersonInvolvementIdUnbranded;
    reason: string;
  };

export const ReplaceResponsibleUserRequestSchema =
  ResponsibleUserNamedSchema.extend({
    involvementId: PersonInvolvementIdSchema,
    reason: z
      .string()
      .trim()
      .min(1, "involvement.ending.reasonRequired")
      .max(4000, "involvement.ending.reasonTooLong"),
  }).brand<"haven-common:ReplaceResponsibleUserRequest">() satisfies ZodType<ReplaceResponsibleUserRequestUnbranded>;

/**
 * Handing a case that somebody runs to a successor: who takes it on, the body
 * they run it for, and why it is changing hands. The outgoing holder ends and
 * the successor starts together, so the case is never without somebody running
 * it.
 */
export type ReplaceResponsibleUserRequest = z.infer<
  typeof ReplaceResponsibleUserRequestSchema
>;

/**
 * A young person one party can reach, and the involvement that gives them it.
 *
 * The same rows as the access view, read from the other end — the question
 * asked when somebody leaves, or when a child's authority is asked who has seen
 * their record.
 */
export type PartyReachUnbranded = {
  personId: PersonIdUnbranded;
  personLabel: string;
  kind: PersonInvolvementKind;
  startedAt: Date;
  endedAt: Date | null;
  actions: ConveyedPersonAction[];
};

export const PartyReachSchema = z.object({
  personId: PersonIdSchema,
  personLabel: z.string(),
  kind: PersonInvolvementKindSchema,
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().nullable(),
  actions: z.array(ConveyedPersonActionSchema),
}) satisfies ZodType<PartyReachUnbranded>;

export type PartyReach = z.infer<typeof PartyReachSchema>;

/**
 * One way a session reaches a young person's record: the involvement that puts
 * it in reach, whether the organisation acted for or the person acting holds
 * it, and whether it is held there or by a member below. A child reached
 * several ways has one route for each.
 *
 * Having registered a record is `created_by`, the involvement stating the
 * same fact, so having opened a record has one name however it is recorded.
 */
export type ViewRouteUnbranded = {
  kind: PersonInvolvementKind;
  heldBy: "organisation" | "user";
  via: "direct" | "member-of";
};

export const ViewRouteSchema = z.object({
  kind: PersonInvolvementKindSchema,
  heldBy: z.enum(["organisation", "user"]),
  via: z.enum(["direct", "member-of"]),
}) satisfies ZodType<ViewRouteUnbranded>;

export type ViewRoute = z.infer<typeof ViewRouteSchema>;

/**
 * Where each involvement falls in the young people's listing, in the order of
 * its label, so a row's own roles read alphabetically and sorting the column
 * sorts what the cells say.
 */
export const LISTING_INVOLVEMENT_RANK: Record<PersonInvolvementKind, number> = {
  approver: 0,
  assessor: 1,
  designated_assessor: 2,
  duty_of_care: 3,
  created_by: 4,
  viewer: 5,
  placement: 6,
  shared_assessment_reader: 7,
  responsible_user: 8,
};

/** Seeing a child only through one's organisation, ranked after every role. */
export const LISTING_VIEWER_THROUGH_ORGANISATION_RANK = 9;

/**
 * One contributing assessor, as enrolment names them.
 *
 * `organisationId` is absent for a guest — somebody with no body behind them,
 * which a contributing assessor is the only involvement anybody may be.
 */
export type EnrolledAssessorUnbranded = {
  userId: UserIdUnbranded;
  organisationId: OrganisationIdUnbranded | null;
};

export const EnrolledAssessorSchema = z.object({
  userId: UserIdSchema,
  organisationId: OrganisationIdSchema.nullable(),
}) satisfies ZodType<EnrolledAssessorUnbranded>;

export type EnrolledAssessor = z.infer<typeof EnrolledAssessorSchema>;

export type EnrolPersonRequestUnbranded = {
  dutyOfCareOrganisationId: OrganisationIdUnbranded;
  placementOrganisationId: OrganisationIdUnbranded | null;
  designatedAssessorUserId: UserIdUnbranded;
  designatedAssessorOrganisationId: OrganisationIdUnbranded;
  approverUserId: UserIdUnbranded;
  approverOrganisationId: OrganisationIdUnbranded;
  assessors: EnrolledAssessorUnbranded[];
};

export const EnrolPersonRequestSchema = z
  .object({
    dutyOfCareOrganisationId: OrganisationIdSchema,
    placementOrganisationId: OrganisationIdSchema.nullable(),
    designatedAssessorUserId: UserIdSchema,
    designatedAssessorOrganisationId: OrganisationIdSchema,
    approverUserId: UserIdSchema,
    approverOrganisationId: OrganisationIdSchema,
    assessors: z.array(EnrolledAssessorSchema),
  })
  .refine(
    (request) => request.approverUserId !== request.designatedAssessorUserId,
    {
      // An approval by the person whose work it is is not an approval, and an
      // approver exists for no other reason.
      message: "involvement.approver.notTheAssessor",
      path: ["approverUserId"],
    },
  )
  .brand<"haven-common:EnrolPersonRequest">() satisfies ZodType<EnrolPersonRequestUnbranded>;

/** An involvement as a request's sender saw it, for refusing a stale change. */
export type SeenInvolvementUnbranded = {
  involvementId: PersonInvolvementIdUnbranded;
  endedAt: string | null;
};

export const SeenInvolvementSchema = z.object({
  involvementId: PersonInvolvementIdSchema,
  endedAt: z.iso.datetime("involvement.ending.endedAtInvalid").nullable(),
}) satisfies ZodType<SeenInvolvementUnbranded>;

export type SeenInvolvement = z.infer<typeof SeenInvolvementSchema>;

/**
 * Setting a case up: the involvements it cannot run without, opened together.
 *
 * Not a new kind of record — enrolment writes exactly the rows naming each
 * party separately would write. What it adds is a **completeness rule** that
 * per-row writes cannot state: a partial unique index says at most one duty
 * holder, one designation and one approver, and only a transaction can say at
 * least one.
 *
 * Four parties are required, and between them they are what a case needs to
 * run: somebody accountable for the child, somebody whose assessment will
 * stand, and somebody answerable for approving it. The approver's row names the
 * assessor it approves, so it cannot be written before the designation exists —
 * writing them in one act is the only way either can reference the other.
 *
 * The placement is not required: a child may be placed after referral, and
 * where they are is not what stops a case running.
 *
 * `assessors` is the one part that is a list. Contributing assessors are
 * unbounded and may be named at any point; taking them here spares a case that
 * already knows its contributors a write per person, and an empty list is the
 * ordinary state.
 */
export type EnrolPersonRequest = z.infer<typeof EnrolPersonRequestSchema>;

export type EndInvolvementsRequestUnbranded = {
  /**
   * Each involvement as the person ending it saw it: which one, and the ending
   * it had then (`null` for none). The set is refused whole if any of them has
   * changed since, so nobody ends what they have not seen.
   */
  involvements: SeenInvolvementUnbranded[];
  cause: OfferedEndingCause;
  /**
   * The UK calendar day, today or later, at whose end the involvements stop
   * conveying anything, or `null` to end them at once (see
   * `endingTakesEffectAt`).
   */
  endsOn: string | null;
  /**
   * Why, in the words of whoever ended them, or `null` where the cause says
   * enough (see `isReasonNeededForEndingCause`). Prose about a child, so it is held on the
   * record beside the ending and never written to the trail or a log.
   */
  reason: string | null;
};

export const EndInvolvementsRequestSchema = z
  .object({
    involvements: z
      .array(SeenInvolvementSchema)
      .min(1, "involvement.ending.nothingChosen"),
    cause: OfferedEndingCauseSchema,
    endsOn: z.iso
      .date({ error: "involvement.ending.endsOnInvalid", abort: true })
      .refine(
        (day) => !isLaterDay(today(), day),
        "involvement.ending.endsOnPast",
      )
      .nullable(),
    // Blank is no reason at all, so it is stored as none.
    reason: z
      .string()
      .trim()
      .max(4000, "involvement.ending.reasonTooLong")
      .transform((reason) => (reason === "" ? null : reason))
      .nullable(),
  })
  .refine(
    (ending) =>
      ending.reason !== null || !isReasonNeededForEndingCause(ending.cause),
    { message: "involvement.ending.reasonRequired", path: ["reason"] },
  )
  .brand<"haven-common:EndInvolvementsRequest">() satisfies ZodType<EndInvolvementsRequestUnbranded>;

/**
 * Ending a set of involvements together, with one cause, one ending time, one
 * reason and one author. The set is exactly what the person ending chose;
 * nothing here works out what ought to end.
 */
export type EndInvolvementsRequest = z.infer<
  typeof EndInvolvementsRequestSchema
>;

/** Somebody acting for one organisation, in the roles it combines there. */
export type ActingParty = {
  userId: UserId;
  organisationId: OrganisationId;
  roles: readonly CustomerRole[];
  /**
   * Every organisation that is a member of the one acted for, directly or
   * through another. Absent is none.
   */
  memberOrganisationIds?: readonly OrganisationId[] | undefined;
  /**
   * Whether the organisation acted for reads what a finished assessment says
   * about a young person it reaches only through a member. Absent is not.
   */
  readsApprovedAssessmentsThroughMembers?: boolean | undefined;
};

/** A record and every record merged into it, as far as who registered each. */
export type PersonRecordCreators = {
  createdByOrganisationId: OrganisationId;
  mergedPersonRecords: readonly {
    createdByOrganisationId: OrganisationId;
  }[];
};

/** What can travel up a membership. */
export type CarriedUp = "view" | "admin" | "approved-assessments";

export const CarriedUpSchema = z.enum([
  "view",
  "admin",
  "approved-assessments",
]) satisfies ZodType<CarriedUp>;
